import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ArrowUp, ArrowDown, Upload } from "lucide-react";

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/gallery", { photo_path: up.path, caption: "" });
      toast.success("Added to gallery");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Removed");
    load();
  };

  const move = async (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= items.length) return;
    const reordered = [...items];
    const [m] = reordered.splice(idx, 1);
    reordered.splice(next, 0, m);
    setItems(reordered);
    await api.post("/gallery/reorder", {
      ordered_ids: reordered.map((i) => i.id),
    });
    toast.success("Reordered");
  };

  return (
    <div data-testid="gallery-tab">
      <div className="brutal-card p-6 bg-white mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <div>
          <h3 className="font-heading font-black text-2xl">Public Gallery</h3>
          <p className="text-sm font-medium text-[#4A4A4A]">
            These photos appear on the public homepage. Upload, delete, and reorder as needed.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform cursor-pointer">
          <Upload size={16} />
          {uploading ? "Uploading..." : "Upload Photo"}
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="gallery-upload-input"
          />
        </label>
      </div>

      {items.length === 0 && (
        <div className="brutal-card p-10 text-center font-medium text-[#4A4A4A]">
          No photos yet. Upload one to start filling the gallery.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((g, idx) => (
          <div
            key={g.id}
            className="brutal-card p-0 bg-white overflow-hidden"
            data-testid={`gallery-item-${g.id}`}
          >
            <img
              src={fileUrl(g.photo_path)}
              alt={g.caption || "Gallery"}
              className="w-full h-56 object-cover border-b-2 border-[#0A0A0A]"
            />
            <div className="p-4 flex items-center justify-between gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`gallery-up-${g.id}`}
                  className="p-2 border-2 border-[#0A0A0A] rounded-full bg-white disabled:opacity-40 hover:shadow-brutal-sm transition-shadow"
                >
                  <ArrowUp size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  data-testid={`gallery-down-${g.id}`}
                  className="p-2 border-2 border-[#0A0A0A] rounded-full bg-white disabled:opacity-40 hover:shadow-brutal-sm transition-shadow"
                >
                  <ArrowDown size={14} strokeWidth={2.5} />
                </button>
              </div>
              <button
                onClick={() => del(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
              >
                <Trash2 size={14} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
